// Levels and quests.
import { QuestObjectiveKind, QuestStatus } from '../constants';
import { type GameContent, type MobDefinition, type QuestDefinition, type QuestObjective } from '../content';
import { Inventory } from './items';
import { clamp, f32, roundHalfEven } from './numbers';

export const Leveling = {
  maxLevel: 50,
  /** A level 1 character's health and mana before talents. */
  baseHealth: 100,
  baseMana: 50,
  expLevelRange: 5,
  healthPerLevel: 10,
  manaPerLevel: 5,

  /** Exp needed to go from level to the next (0 at the cap). */
  expToNext: (level: number) => (level >= 50 ? 0 : roundHalfEven(f32(25 * f32(Math.pow(Math.max(1, level), f32(1.6)))))),
  clampLevel: (level: number) => clamp(level, 1, 50),
  earnsExp: (player: number, mob: number) => Math.abs(player - mob) <= 5,
  mobExp: (def: MobDefinition, level: number) => {
    const l = Leveling.clampLevel(level);
    return roundHalfEven(f32(def.baseExp * l * f32(1 + f32(f32(0.1) * (l - 1)))));
  },
  mobMaxHealth: (def: MobDefinition, level: number) =>
    roundHalfEven(f32(def.maxHealth * f32(1 + f32(f32(0.35) * (Leveling.clampLevel(level) - 1))))),
  mobContactDamage: (def: MobDefinition, level: number) =>
    roundHalfEven(f32(def.contactDamage * f32(1 + f32(f32(0.2) * (Leveling.clampLevel(level) - 1))))),
  /** Copper a mob drops for each tagger; roll is a uniform draw in [0, 1). */
  mobCoins: (def: MobDefinition, level: number, roll: number) => def.coinsMax <= 0 ? 0
    : roundHalfEven((def.coinsMin + (def.coinsMax - def.coinsMin) * clamp(roll, 0, 1)) * Leveling.clampLevel(level)),
  playerMaxHealth: (base: number, level: number) => base + (Leveling.clampLevel(level) - 1) * 10,
  playerMaxMana: (base: number, level: number) => base + (Leveling.clampLevel(level) - 1) * 5,

  /** Adds exp, levelling up as many times as it covers; returns [level, exp within it]. */
  addExp(level: number, exp: number, amount: number): [number, number] {
    level = Leveling.clampLevel(level);
    exp = Math.max(0, exp) + Math.max(0, amount);
    while (level < 50 && exp >= Leveling.expToNext(level)) {
      exp -= Leveling.expToNext(level);
      level++;
    }
    return level >= 50 ? [50, 0] : [level, exp];
  },
};

// ---- quests ------------------------------------------------------------------------------------------------------

export const allQuests = (c: GameContent): QuestDefinition[] => [...c.quests.values()];
export const questsGivenBy = (npc: number, c: GameContent) => allQuests(c).filter(q => q.giver === npc);

function targetName(o: QuestObjective, c: GameContent): string {
  const name = o.kind === QuestObjectiveKind.Kill ? c.mob(o.target)?.name
    : o.kind === QuestObjectiveKind.Talk ? c.npc(o.target)?.name : c.item(o.target)?.name;
  return name ?? String(o.target);
}

/** The task without its count ("Gather Wood Log"), for UIs that show progress in a column. */
export function describeTask(o: QuestObjective, c: GameContent): string {
  switch (o.kind) {
    case QuestObjectiveKind.Collect: return `Bring ${targetName(o, c)}`;
    case QuestObjectiveKind.Kill: return `Slay ${targetName(o, c)}`;
    case QuestObjectiveKind.Talk: return `Talk to ${targetName(o, c)}`;
    case QuestObjectiveKind.Gather: return `Gather ${targetName(o, c)}`;
    case QuestObjectiveKind.Craft: return `Craft ${targetName(o, c)}`;
    case QuestObjectiveKind.Equip: return `Equip ${targetName(o, c)}`;
    default: return String(o.target);
  }
}

/** An objective as a line ("Slay Slime x3"). */
export function describeObjective(o: QuestObjective, c: GameContent): string {
  return o.kind === QuestObjectiveKind.Talk || !(o.kind in QuestObjectiveKind) ? describeTask(o, c) : `${describeTask(o, c)} x${o.count}`;
}

/** Current progress of one objective (Collect: items in the bag, capped at the goal). */
export const progressOf = (o: QuestObjective, stored: number, inv: Inventory) =>
  clamp(o.kind === QuestObjectiveKind.Collect ? inv.countInBag(o.target)
    : o.kind === QuestObjectiveKind.Equip ? [...inv.belt, ...inv.armor].filter(s => s.item === o.target && s.count > 0).length : stored, 0, o.count);

export function objectivesMet(q: QuestDefinition, progress: readonly number[], inv: Inventory): boolean {
  return q.objectives.every((o, i) => progressOf(o, i < progress.length ? progress[i] : 0, inv) >= o.count);
}

/** Where a quest stands; progress is the active quest's per-objective progress (null when not active). */
export function questStatus(q: QuestDefinition, isCompleted: (id: number) => boolean, progress: readonly number[] | null, inv: Inventory): QuestStatus {
  if (isCompleted(q.id)) return QuestStatus.Completed;
  if (progress !== null) return objectivesMet(q, progress, inv) ? QuestStatus.Completable : QuestStatus.Active;
  return q.requires === 0 || isCompleted(q.requires) ? QuestStatus.Offerable : QuestStatus.Locked;
}
